import type { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { SeverityLevel } from '@microsoft/applicationinsights-web';

// EWM-954 - one place every client-side crash goes.
//
// Crashes used to go to Dash0 as well. That was the only sink until the 2026-08-18 convert crash in
// TEST reached us through a user rather than an alert: the team's alerting lives in App Insights /
// Log Analytics - the alert rules, the KQL runbooks, the /triage pass - and `AppExceptions` was
// empty for the whole 12 hours around it. A signal only in the system nobody queries for this class
// is not monitoring.
//
// EWM-1051 then removed Dash0 entirely, once App Insights was verified to carry every signal it
// had: client crashes, browser->API distributed traces, AJAX dependencies and page views. This is
// now the single sink.

let telemetryClient: ApplicationInsights | null = null;

// EWM-1051: the SDK loads asynchronously and the provider hands the client over only afterwards, so
// there is a window at startup with nowhere to send. Dash0 used to absorb it - it initialised
// synchronously in main.tsx - and dropping it without a buffer would have quietly lost exactly the
// early failures that matter most: the auth and initialization paths run inside that window, and
// `ClientAuthError` on DEV is the most frequent client exception we have.
//
// Bounded on purpose: if the client never arrives (no connection string configured), this must not
// grow without limit.
const MAX_PENDING = 20;
let pending: { error: Error; context: CrashContext }[] = [];

type CrashContext = {
  /** Where it came from: the React boundary, an uncaught error, a rejected promise, MSAL. */
  source:
    | 'react-error-boundary'
    // EWM-1100: the last-resort boundary above the provider tree, distinct from the router's so a
    // crash that took the whole app down is separable in telemetry from one that took a route down.
    | 'react-root-boundary'
    | 'window-onerror'
    | 'unhandled-rejection'
    | 'auth-failure'
    | 'console-error'
    | 'resource-load';
  componentStack?: string;
  [key: string]: unknown;
};

const send = (client: ApplicationInsights, crash: Error, context: CrashContext): void => {
  client.trackException({
    exception: crash,
    severityLevel: SeverityLevel.Error,
    properties: {
      // The route is what makes an exception actionable - a stack alone does not say which page
      // the user could not open. Spread last so a queued crash keeps the route it was captured
      // on; by flush time the user has usually navigated away, and the wrong route is worse than
      // none.
      route: window.location.pathname,
      search: window.location.search,
      ...context,
    },
  });
};

/** Called once by AppInsightsContextProvider after the SDK loads. */
export const setCrashReportingClient = (client: ApplicationInsights | null): void => {
  telemetryClient = client;

  if (!client) return;
  const queued = pending;
  pending = [];
  queued.forEach(({ error, context }) => send(client, error, context));
};

export const reportCrash = (error: unknown, context: CrashContext): void => {
  const crash = error instanceof Error ? error : new Error(String(error));

  if (!telemetryClient) {
    // Route captured here rather than at flush time - by then the user has usually navigated on,
    // and the wrong route is worse than none.
    if (pending.length < MAX_PENDING) {
      pending.push({
        error: crash,
        context: { ...context, route: window.location.pathname, search: window.location.search },
      });
    }
    return;
  }

  send(telemetryClient, crash, context);
};

/**
 * A named diagnostic event, for a condition worth a rate rather than a stack (EWM-500).
 * Lands in `customEvents`, not `AppExceptions`.
 */
export const reportEvent = (name: string, properties?: Record<string, unknown>): void => {
  telemetryClient?.trackEvent({ name }, { ...properties, route: window.location.pathname });
};

// There is no installGlobalCrashHandlers here - main.tsx owns the window listeners and reports
// through reportCrash, same as the account app.

// EWM-1055 - console.error is the one reporting path the lint rule leaves open.
//
// `eslint.config.mjs` sets `'no-console': ['error', { allow: ['error'] }]`, so console.error is
// legal and there are 41 call sites in this app (18 in account) - including the ones that matter,
// like the token-renewal and tenant-refresh failures in context/auth. Every one of them writes to
// the user's devtools and nowhere else. EWM-500 closed exactly this gap for the two console.log
// calls ESLint had flagged; the larger set the rule permits was never in scope.
//
// Patching the sink rather than editing 41 call sites is deliberate: a call site added tomorrow is
// covered too, which a one-by-one migration cannot promise.

const formatArg = (arg: unknown): string => {
  if (typeof arg === 'string') return arg;
  if (arg instanceof Error) return arg.message;
  try {
    return JSON.stringify(arg) ?? String(arg);
  } catch {
    // Circular structures are the norm for axios and React error objects.
    return String(arg);
  }
};

/** Guards against console.error -> reportCrash -> App Insights -> console.error recursing forever. */
let reportingConsoleError = false;

let consoleCaptureInstalled = false;

/**
 * Forward every console.error to telemetry, keeping the original devtools output intact.
 * Call once from main.tsx, before the app renders.
 */
export const installConsoleErrorCapture = (): void => {
  if (consoleCaptureInstalled) return;
  consoleCaptureInstalled = true;

  const original = console.error;

  console.error = (...args: unknown[]): void => {
    original(...args);

    if (reportingConsoleError) return;
    reportingConsoleError = true;
    try {
      // Prefer a real Error among the arguments - it carries the stack that says where this came
      // from. `console.error('Token renewal failed:', error)` is the common shape.
      const cause = args.find((arg): arg is Error => arg instanceof Error);
      const message = args.map(formatArg).join(' ');

      reportCrash(cause ?? new Error(message), {
        source: 'console-error',
        // Kept even when `cause` supplies the exception, so the surrounding text ("Token renewal
        // failed") is not lost - it is usually what identifies the call site.
        message,
      });
    } catch {
      // Never let telemetry break the caller. Most of the call sites we now intercept are inside
      // catch blocks, so a throw here would turn a handled error into an unhandled one - this
      // wrapper would manufacture exactly the crashes it exists to observe. The original console
      // output has already happened above, so nothing is lost by swallowing.
    } finally {
      reportingConsoleError = false;
    }
  };
};
